import Error from '@pages/Error';
import BetaBanner from 'components/Banner/Beta';
import Layout from 'components/Layout';
import { IMAGE_SERVICE_PATH, IMAGE_SERVICE_URL } from 'config';
import { formatMoney } from 'helpers/formatters/number';
import { getTranslationForCoinDescription } from 'helpers/translation';
import Head from 'next/head';
import { SingletonRouter, withRouter } from 'next/router';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Coin } from 'store/Coin/types';
import { Currency, USD } from 'store/Currency/types';
import withCurrency from 'store/Currency/with-currency';

import Analysis from './Analysis';
import withContainer from './container';
import Direct from './Direct';
import Header from './Header';
import Info from './Info';
import MarketCharts from './MarketCharts';
import {
  AnalysisWrapper,
  DirectWrapper,
  HeaderWrapper,
  InfoWrapper,
} from './styles';

interface Props {
  coin: Coin | null;
  currency: Currency;
  router: SingletonRouter;
}

const MarketsDetail = (props: Props) => {
  const { t } = useTranslation();
  const { coin, currency, router } = props;
  const { asPath } = router;

  if (!coin) {
    return <Error />;
  }

  const title = `${coin.name} (${coin.code.toUpperCase()})${
    coin.priceInUSD ? `: ${formatMoney(coin.priceInUSD, currency)}` : ''
  }`;
  const description = getTranslationForCoinDescription(t, coin, currency);
  const imageUrl = `${IMAGE_SERVICE_URL}/${coin.id}/social.jpg`;

  return (
    <Layout topChildren={<BetaBanner />} footerGradient removeLanguagePicker>
      <Head>
        <title>{title}</title>
        <link
          key="favicon"
          rel="icon"
          href={`${IMAGE_SERVICE_PATH}/${coin.id}/icon-32.png`}
        />
        <meta key="description" name="description" content={description} />
        <meta key="og:title" property="og:title" content={title} />
        <meta
          key="og:description"
          property="og:description"
          content={description}
        />
        <meta key="twitter:title" name="twitter:title" content={title} />
        <meta
          key="twitter:description"
          name="twitter:description"
          content={description}
        />
        <meta key="og:image" property="og:image" content={imageUrl} />
        <meta key="twitter:image" name="twitter:image" content={imageUrl} />
        <meta
          key="twitter:card"
          name="twitter:card"
          content="summary_large_image"
        />
      </Head>

      {coin.priceInUSD && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Product',
              name: `${coin.name} ${coin.code.toUpperCase()}`,
              image: `${IMAGE_SERVICE_PATH}/${coin.id}/icon-128.png`,
              description,
              offers: {
                '@type': 'Offer',
                url: `${process.env.NEXT_PUBLIC_URL}${asPath}`,
                price: coin.priceInUSD,
                priceCurrency: USD.currency,
              },
            }),
          }}
        />
      )}

      <HeaderWrapper>
        <Header coin={coin} />
      </HeaderWrapper>

      <MarketCharts coin={coin} />
      <Direct Wrapper={DirectWrapper} coin={coin} />
      <Analysis Wrapper={AnalysisWrapper} coin={coin} />
      <Info Wrapper={InfoWrapper} coin={coin} />
    </Layout>
  );
};

export default withContainer(withRouter(withCurrency(MarketsDetail)));
